import { useEffect, useRef } from 'react';
import { useFixedPopover } from 'utilities/useFixedPopover';
import { t } from 'utilities/TranslationUtils';
import './FilterPopover.scss';

interface Option {
  value: string;
  label: string;
}

interface Props {
  options: ReadonlyArray<Option>;
  /** Selected values; an empty array means "all results". */
  selected: ReadonlyArray<string>;
  onChange: (selected: string[]) => void;
  onClose: () => void;
  /** Modal heading; defaults to the result filter's. */
  title?: string;
  /** Pick exactly one option (radio) and close on selection, instead of a multi-select. */
  single?: boolean;
}

/**
 * Small popover (same look as the timeframe date picker's) opened from a filter pill: a multi-select, or with `single` a
 * one-of-many picker. Render it inside a `position: relative` host next to the pill; it closes on outside click / Esc.
 */
export default function FilterPopover({ options, selected, onChange, onClose, title, single = false }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLElement | null>(null);
  const placement = useFixedPopover(true, hostRef, 300);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onPointerDown = (e: MouseEvent) => {
      const host = rootRef.current?.parentElement;
      if (host && !host.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  const effective = single ? [...selected] : selected.length === 0 ? options.map((o) => o.value) : [...selected];
  const toggle = (value: string) => {
    if (single) {
      onChange([value]);
      onClose();
      return;
    }
    const next = effective.includes(value) ? effective.filter((v) => v !== value) : [...effective, value];
    // Everything (or nothing) selected is the canonical "all" state.
    onChange(next.length === 0 || next.length === options.length ? [] : next);
  };

  return (
    <div
      ref={(node) => { rootRef.current = node; hostRef.current = node?.parentElement ?? null; }}
      className='filter-popover'
      style={placement}
      role='dialog'
      data-test='filter-popover'
    >
      <div className='filter-popover__head'>
        <h3>{title ?? t('sessions.previewResult')}</h3>
        <button type='button' className='filter-popover__close' aria-label={t('common.close')} onClick={onClose}>&times;</button>
      </div>
      <div className='filter-popover__list'>
        {options.map((option) => (
          <label key={option.value} className='filter-popover__option'>
            <input type={single ? 'radio' : 'checkbox'} checked={effective.includes(option.value)} onChange={() => toggle(option.value)} />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {!single && (
        <div className='filter-popover__foot'>
          <button type='button' className='filter-popover__link' onClick={() => onChange([])}>{t('sessions.previewAllResults')}</button>
          <button type='button' className='filter-popover__done' onClick={onClose}>{t('common.done')}</button>
        </div>
      )}
    </div>
  );
}
